// 🪪 「새 앱 등록 N명」 알림 표시 (덴클 케어 1차 W3, 2026-10-04 계획_환자앱1차_통합 — 대표 결정 "앱 등록 즉시 성립")
// 종전(10-01 등록 경로 v2)의 "앱 등록 확인 대기 N건" + [확인]·[우리 환자 아님] 카드를 대체한다. 확인 절차가 없어졌으므로 버튼 없이 알림만:
//   최근 7일 안에 환자가 덴클 케어 앱으로 데스크 QR을 찍어 등록한 인원 → [명부 보기](/patients). 직원 추가 작업을 요구하지 않는다.
// variant: 'card'(기본 — 「오늘」 화면) / 'badge'(데스크 「접수」 상단 줄의 작은 배지)
// 노출: config/features.careApp === true(strict)일 때만 — 플래그를 직접 읽어 OFF면 조회도 하지 않는다. 0건이면 아무것도 그리지 않는다.
// 목록 조회 = services/careAppApi.listRecentAppRegistrations(createdAt 범위 where + 상한 200). 콘솔에 환자명 기록 금지 — 오류 코드만.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { getFeatures, isCareAppOn } from '../../utils/features';
import { listRecentAppRegistrations, CARE_NEW_REG_DAYS } from '../../services/careAppApi';

function ClinicSelfRegisterCard({ clinicId, variant = 'card' }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const navigate = useNavigate();
    const [on, setOn] = useState(false);
    const [count, setCount] = useState(0);
    const [err, setErr] = useState('');

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setOn(isCareAppOn(f)); })
            .catch(e => { console.error('[새 앱 등록] 플래그 조회 실패:', e?.code || e?.message || e); });
        return () => { alive = false; };
    }, []);

    const load = useCallback(async () => {
        if (!on || !clinicId) return;
        setErr('');
        try {
            const rows = await listRecentAppRegistrations(clinicId);
            setCount(rows.length);
        } catch (e) {
            console.error('[새 앱 등록] 목록 조회 실패:', e?.code || e?.message || e);
            setErr('새 앱 등록 수를 불러오지 못했습니다.');
        }
    }, [on, clinicId]);

    useEffect(() => { load(); }, [load]);

    if (!on || (count === 0 && !err)) return null;

    if (variant === 'badge') {
        if (err) return <span title={err} style={S.badgeErr}>새 앱 등록 확인 실패</span>;
        return (
            <button type="button" onClick={() => navigate('/patients')} style={S.badge} title={`최근 ${CARE_NEW_REG_DAYS}일 안에 앱으로 등록한 환자 — 명부로 이동`}>
                {`새 앱 등록 ${count}명`}
            </button>
        );
    }

    return (
        <div style={S.card}>
            <div style={S.head}>
                <div style={S.iconBox}><UserPlus size={18} color="#fff" /></div>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={S.title}>{err ? '새 앱 등록' : `새 앱 등록 ${count}명`}</div>
                    <div style={S.notice}>{`최근 ${CARE_NEW_REG_DAYS}일 안에 환자가 덴클 케어 앱에서 데스크 QR을 찍거나 치과 찾기로 우리 치과를 골라 명부에 등록했습니다. 의뢰서에서 고를 때 이름·생년월일·번호 끝 4자리를 함께 봐 주세요.`}</div>
                </div>
                <button type="button" onClick={() => navigate('/patients')} style={S.chip}>명부 보기</button>
            </div>
            {err && <div style={S.err}>{err}</div>}
        </div>
    );
}

const S = {
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', marginBottom: 14, userSelect: 'none' },
    head: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    iconBox: { width: 36, height: 36, borderRadius: 10, background: '#0e7490', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
    notice: { fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.5 },
    chip: { padding: '6px 12px', fontSize: 12.5, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#475569', cursor: 'pointer' },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    badge: { padding: '4px 10px', fontSize: 12.5, fontWeight: 700, border: '1px solid #a5f3fc', borderRadius: 999, background: '#ecfeff', color: '#0e7490', cursor: 'pointer' },
    badgeErr: { padding: '4px 10px', fontSize: 12, border: '1px solid #f5cfcf', borderRadius: 999, background: '#fdecec', color: '#8a2020' },
};

export default ClinicSelfRegisterCard;
